import * as React from "react";
import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Text, Badge, Button, makeStyles, tokens } from "@fluentui/react-components";
import { Open24Regular } from "@fluentui/react-icons";
import { DiscussionDto } from "../../../services/discussions.service";
import { normalizeStatus, getStatusColor, getStatusColorLight } from "../../../utils/discussionStatusHelpers";
import { useTypographyStyles } from "../../../styles/typography.styles";

const useStyles = makeStyles({
  section: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalXXS,
  },
  reviewPointRow: {
    display: "flex",
    alignItems: "center",
    gap: tokens.spacingHorizontalXS,
    padding: `2px 0`,
  },
  reviewPointTitle: {
    flex: 1,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  emptyStatePad: {
    padding: tokens.spacingVerticalXS,
  },
});

interface LiveReviewPointsProps {
  discussions: DiscussionDto[];
}

/**
 * Renders the current live list of open review points (type === "review_point",
 * status not "Resolved"). Used in the Manager and Partner tabs before the
 * respective sign-off freezes the list into a snapshot.
 */
const LiveReviewPoints: React.FC<LiveReviewPointsProps> = ({ discussions }) => {
  const styles = useStyles();
  const typography = useTypographyStyles();
  const navigate = useNavigate();

  const openReviewPoints = useMemo(() => {
    return discussions.filter(
      (d) =>
        d.type === "review_point" &&
        normalizeStatus(d.status).toLowerCase() !== "resolved"
    );
  }, [discussions]);

  const handleGoToDiscussion = (discussionId: number) => {
    // `from=review` lets the discussion thread's back button return here
    // instead of dropping the user onto the discussion list.
    navigate(`/discussions?discussionId=${discussionId}&from=review`);
  };

  return (
    <div className={styles.section}>
      <Text className={typography.subsectionTitle}>
        Open Review Points ({openReviewPoints.length})
      </Text>
      {openReviewPoints.length === 0 ? (
        <Text className={`${typography.emptyStateBody} ${styles.emptyStatePad}`}>
          No open review points.
        </Text>
      ) : (
        openReviewPoints.map((d) => (
          <div key={d.id} className={styles.reviewPointRow}>
            <Text className={`${typography.bodyLarge} ${styles.reviewPointTitle}`}>
              {d.title}
            </Text>
            <Badge
              size="small"
              appearance="filled"
              style={{
                backgroundColor: getStatusColorLight(d.status),
                color: getStatusColor(d.status),
              }}
            >
              {normalizeStatus(d.status)}
            </Badge>
            <Button
              size="small"
              icon={<Open24Regular />}
              appearance="subtle"
              onClick={() => handleGoToDiscussion(d.id)}
            />
          </div>
        ))
      )}
    </div>
  );
};

export default LiveReviewPoints;
